import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Stack, HStack, Button, useToast } from "@chakra-ui/react";
import {
  getLibrariesData,
  updateLibrariesData,
} from "../../../services/Libraries";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import FormLoader from "../../../CommonComponents/FormLoader";
import { useSelector } from "react-redux";
import { handleApiError, showSuccessToast } from "../../../util/errorHandler";

// Renders nothing; keeps the field value in formData (ui:widget "hidden" does not apply to array fields)
const HiddenField = () => null;

const AuctionInfo = () => {
  const { alias } = useParams();
  const [quickSave, setQuickSave] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector((state) => state.preview.isEnabled);
  const toast = useToast();
  const type = window.location.pathname.split("/")[1];

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = [
      "_id",
      "le_inr",
      "he_inr",
      "le_usd",
      "he_usd",
      "le_gbp",
      "he_gbp",
      "sp_inr",
      "sp_usd",
      "sp_gbp",
      "auction_code",
      "imca_lots_offered_quantity",
      "imca_lots_sold_quantity",
      "percentage_of_lots_sold",
      "highlight",
      "start_date",
      "end_date",
    ];
    const rightFields = [
      "city_of_event",
      "type_of_event",
      "lot_number",
      "square_inch_rate_in_inr_terms",
      "year",
      "year_of_exhibition",
      "type_of_creator",
      "type_of_joint_creator",
      "scene_description",
      "type_of_exhibition",
      "venue",
      "auction_house_name",
    ];

    const leftData = {};
    const rightData = {};

    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });

    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });

    return { leftData, rightData };
  };

  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Left column schema
  const leftColumnSchema = {
    title: "",
    type: "object",
    properties: {
      _id: { type: "string" },
      le_inr: {
        type: ["string", "object"],
        title: "LE INR",
      },
      he_inr: {
        type: ["string", "object"],
        title: "HE INR",
      },
      le_usd: {
        type: ["string", "object"],
        title: "LE USD",
      },
      he_usd: {
        type: ["string", "object"],
        title: "HE USD",
      },
      le_gbp: {
        type: ["string", "object"],
        title: "LE GBP",
      },
      he_gbp: {
        type: ["string", "object"],
        title: "HE GBP",
      },
      sp_inr: {
        type: ["string", "object"],
        title: "SP INR",
      },
      sp_usd: {
        type: ["string", "object"],
        title: "SP USD",
      },
      sp_gbp: {
        type: ["string", "object"],
        title: "SP GBP",
      },
      auction_code: {
        type: "string",
        title: "Auction Code",
      },
      imca_lots_offered_quantity: {
        type: "string",
        title: "IMCA Lots Offered Quantity",
      },
      imca_lots_sold_quantity: {
        type: "string",
        title: "IMCA Lots Sold Quantity",
      },
      percentage_of_lots_sold: {
        type: "string",
        title: "Percentage of Lots Sold",
      },
      highlight: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Highlight",
      },
      start_date: {
        type: ["string", "null"],
        title: "Start date",
        format: "date",
      },
      end_date: {
        type: ["string", "null"],
        title: "End date",
        format: "date",
      },
    },
  };

  // Right column schema
  const rightColumnSchema = {
    title: "",
    type: "object",
    properties: {
      city_of_event: {
        type: "string",
        title: "City of event",
      },
      type_of_event: {
        type: "string",
        title: "Type of event",
      },
      lot_number: {
        type: ["number", "null"],
        title: "Lot number",
        minimum: 1,
      },
      square_inch_rate_in_inr_terms: {
        type: "string",
        title: "Square inch rate in INR terms",
      },
      year: {
        type: "string",
        title: "Year",
      },
      year_of_exhibition: {
        type: "string",
        title: "Year of exhibition",
      },
      type_of_creator: {
        type: "string",
        title: "Type of creator",
      },
      type_of_joint_creator: {
        type: "string",
        title: "Type of joint creator",
      },
      scene_description: {
        type: "string",
        title: "Scene description",
      },
      type_of_exhibition: {
        type: "string",
        title: "Type of exhibition",
      },
      venue: {
        type: "string",
        title: "Venue",
      },
      auction_house_name: {
        type: "string",
        title: "Auction house name",
      },
    },
  };

  // Combined schema for preview mode
  const combinedSchema = {
    title: "Auction Info Fields",
    description: "",
    type: "object",
    properties: {
      _id: { type: "string" },
      le_inr: {
        type: ["string", "object"],
        title: "LE INR",
      },
      he_inr: {
        type: ["string", "object"],
        title: "HE INR",
      },
      le_usd: {
        type: ["string", "object"],
        title: "LE USD",
      },
      he_usd: {
        type: ["string", "object"],
        title: "HE USD",
      },
      le_gbp: {
        type: ["string", "object"],
        title: "LE GBP",
      },
      he_gbp: {
        type: ["string", "object"],
        title: "HE GBP",
      },
      sp_inr: {
        type: ["string", "object"],
        title: "SP INR",
      },
      sp_usd: {
        type: ["string", "object"],
        title: "SP USD",
      },
      sp_gbp: {
        type: ["string", "object"],
        title: "SP GBP",
      },
      auction_code: {
        type: "string",
        title: "Auction Code",
      },
      imca_lots_offered_quantity: {
        type: "string",
        title: "IMCA Lots Offered Quantity",
      },
      imca_lots_sold_quantity: {
        type: "string",
        title: "IMCA Lots Sold Quantity",
      },
      percentage_of_lots_sold: {
        type: "string",
        title: "Percentage of Lots Sold",
      },
      highlight: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Highlight",
      },
      start_date: {
        type: ["string", "null"],
        title: "Start date",
        format: "date",
      },
      end_date: {
        type: ["string", "null"],
        title: "End date",
        format: "date",
      },
      city_of_event: {
        type: "string",
        title: "City of event",
      },
      type_of_event: {
        type: "string",
        title: "Type of event",
      },
      lot_number: {
        type: ["number", "null"],
        title: "Lot number",
        minimum: 1,
      },
      square_inch_rate_in_inr_terms: {
        type: "string",
        title: "Square inch rate in INR terms",
      },
      year: {
        type: "string",
        title: "Year",
      },
      year_of_exhibition: {
        type: "string",
        title: "Year of exhibition",
      },
      type_of_creator: {
        type: "string",
        title: "Type of creator",
      },
      type_of_joint_creator: {
        type: "string",
        title: "Type of joint creator",
      },
      scene_description: {
        type: "string",
        title: "Scene description",
      },
      type_of_exhibition: {
        type: "string",
        title: "Type of exhibition",
      },
      venue: {
        type: "string",
        title: "Venue",
      },
      auction_house_name: {
        type: "string",
        title: "Auction house name",
      },
    },
  };

  // Left column UI schema
  const leftColumnUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    le_inr: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    he_inr: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    le_usd: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    he_usd: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    le_gbp: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    he_gbp: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    sp_inr: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    sp_usd: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    sp_gbp: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    auction_code: { "ui:emptyValue": "" },
    imca_lots_offered_quantity: { "ui:emptyValue": "" },
    imca_lots_sold_quantity: { "ui:emptyValue": "" },
    percentage_of_lots_sold: { "ui:emptyValue": "" },
    highlight: { "ui:field": HiddenField },
    start_date: {
      "ui:emptyValue": null,
      "ui:widget": "date",
      "ui:options": {
        allowClear: true,
      },
    },
    end_date: {
      "ui:emptyValue": null,
      "ui:widget": "date",
      "ui:options": {
        allowClear: true,
      },
    },
  };

  // Right column UI schema
  const rightColumnUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    "ui:style": {
      fontSize: "14px",
    },
    city_of_event: { "ui:emptyValue": "" },
    type_of_event: { "ui:emptyValue": "" },
    lot_number: { "ui:widget": "hidden", "ui:emptyValue": null },
    square_inch_rate_in_inr_terms: { "ui:widget": "hidden" },
    year: { "ui:emptyValue": "" },
    year_of_exhibition: { "ui:emptyValue": "" },
    type_of_creator: { "ui:emptyValue": "" },
    type_of_joint_creator: { "ui:emptyValue": "" },
    scene_description: {
      "ui:emptyValue": "",
      "ui:widget": "textarea",
    },
    type_of_exhibition: { "ui:emptyValue": "" },
    venue: { "ui:emptyValue": "" },
    auction_house_name: { "ui:emptyValue": "" },
  };

  // Combined UI schema for preview mode
  const combinedUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    le_inr: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    he_inr: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    le_usd: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    he_usd: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    le_gbp: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    he_gbp: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    sp_inr: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    sp_usd: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    sp_gbp: {
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text",
      },
    },
    auction_code: { "ui:emptyValue": "" },
    imca_lots_offered_quantity: { "ui:emptyValue": "" },
    imca_lots_sold_quantity: { "ui:emptyValue": "" },
    percentage_of_lots_sold: { "ui:emptyValue": "" },
    highlight: { "ui:field": HiddenField },
    start_date: {
      "ui:emptyValue": null,
      "ui:widget": "date",
      "ui:options": {
        allowClear: true,
      },
    },
    end_date: {
      "ui:emptyValue": null,
      "ui:widget": "date",
      "ui:options": {
        allowClear: true,
      },
    },
    city_of_event: { "ui:emptyValue": "" },
    type_of_event: { "ui:emptyValue": "" },
    lot_number: { "ui:widget": "hidden", "ui:emptyValue": null },
    square_inch_rate_in_inr_terms: { "ui:widget": "hidden" },
    year: { "ui:emptyValue": "" },
    year_of_exhibition: { "ui:emptyValue": "" },
    type_of_creator: { "ui:emptyValue": "" },
    type_of_joint_creator: { "ui:emptyValue": "" },
    scene_description: {
      "ui:emptyValue": "",
      "ui:widget": "textarea",
    },
    type_of_exhibition: { "ui:emptyValue": "" },
    venue: { "ui:emptyValue": "" },
    auction_house_name: { "ui:emptyValue": "" },
  };

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getLibrariesData(alias, "auction-info", type);
          if (result) {
            // Process the data to fix object values
            const data = result?.data?.[0] || {};

            // Fix fields that might be objects
            const stringFields = [
              "le_inr",
              "he_inr",
              "le_usd",
              "he_usd",
              "le_gbp",
              "he_gbp",
              "sp_inr",
              "sp_usd",
              "sp_gbp",
            ];

            stringFields.forEach((field) => {
              if (data[field]) {
                // Handle MongoDB's $numberDecimal format
                if (
                  typeof data[field] === "object" &&
                  data[field].$numberDecimal
                ) {
                  data[field] = data[field].$numberDecimal;
                }
                // Handle other object formats
                else if (typeof data[field] === "object") {
                  data[field] = JSON.stringify(data[field]);
                }
              }
            });

            // Process date fields - ensure they're in the right format for the date picker
            ["start_date", "end_date"].forEach((field) => {
              if (data[field]) {
                // Try to format the date as YYYY-MM-DD for the date picker
                try {
                  const date = new Date(data[field]);
                  if (!isNaN(date.getTime())) {
                    // Format as YYYY-MM-DD
                    data[field] = date.toISOString().split("T")[0];
                  }
                } catch (e) {
                  console.warn(`Failed to format ${field} as date:`, e);
                }
              }
            });

            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(data);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(data);
            setQuickSave(false);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data",
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      let finalFormData;

      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }

      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      // Create a copy to avoid mutation
      const processedFormData = { ...finalFormData };

      // Process string fields that might need parsing
      const stringFields = [
        "le_inr",
        "he_inr",
        "le_usd",
        "he_usd",
        "le_gbp",
        "he_gbp",
        "sp_inr",
        "sp_usd",
        "sp_gbp",
      ];

      stringFields.forEach((field) => {
        const value = processedFormData[field];

        // Skip empty values
        if (!value) return;

        // If it's a valid number, convert to number
        if (!isNaN(parseFloat(value)) && isFinite(value)) {
          processedFormData[field] = parseFloat(value);
        }
        // If it looks like JSON, try to parse it
        else if (
          typeof value === "string" &&
          (value.startsWith("{") || value.startsWith("["))
        ) {
          try {
            const parsed = JSON.parse(value);
            // If it's a MongoDB decimal, extract the value
            if (parsed.$numberDecimal) {
              processedFormData[field] = parseFloat(parsed.$numberDecimal);
            } else {
              processedFormData[field] = parsed;
            }
          } catch (e) {
            // If parsing fails, keep it as a string
            console.warn(`Failed to parse ${field} as JSON:`, e);
          }
        }
      });

      // Process date fields
      ["start_date", "end_date"].forEach((field) => {
        // If empty, set to null
        if (!processedFormData[field]) {
          processedFormData[field] = null;
        }
      });

      if (alias) {
        const result = await updateLibrariesData(
          alias,
          processedFormData,
          "",
          "auction-info",
        );

        if (result?.status === 200) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <Box fontSize="lg" fontWeight="bold" mb={4}>
                  Auction Info Fields
                </Box>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  fontsize={"14px"}
                />
              </Box>
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection="row"
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Box fontSize="lg" fontWeight="bold" mb={4}>
                      Auction Pricing & Dates
                    </Box>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AuctionInfo;
